import { fetchAllPvSize } from './allPvSizeService';

export type DgStatsCoverageStatus = 'available' | 'incomplete' | 'unavailable' | 'unknown';

export type DgStatsCoverageLevel = 'AVAILABLE' | 'INCOMPLETE' | 'UNAVAILABLE';

export type DgStatsCoverageFacts = {
  coverageStatus: DgStatsCoverageLevel;
  coverageReason: string;
  coveredUtility: string | null;
  sourceDataset: string;
  sourceLatestDate: number | null;
};

/**
 * DGStats / all_pv_size has no complete coverage for many publicly owned utility (POU) communities.
 * Absence of rows means coverage unavailable — not zero projects or capacity.
 */
export function isDgStatsCoverageAbsent(rows: unknown[] | null | undefined): boolean {
  return !rows || rows.length === 0;
}

export function mapLegacyCoverageStatus(
  status: DgStatsCoverageStatus
): DgStatsCoverageLevel {
  if (status === 'available') return 'AVAILABLE';
  if (status === 'incomplete') return 'INCOMPLETE';
  return 'UNAVAILABLE';
}

export async function resolveDgStatsCoverageForGeofips(
  geofips: number | null | undefined
): Promise<DgStatsCoverageStatus> {
  if (geofips == null || !Number.isFinite(Number(geofips))) return 'unknown';
  const { data, error } = await fetchAllPvSize({
    select: 'year',
    geofips: Number(geofips),
    orderByYear: false,
  });
  if (error) return 'unavailable';
  return isDgStatsCoverageAbsent(data) ? 'unavailable' : 'available';
}

export async function resolveSolarDgStatsCoverageFacts(
  geofips: number | null | undefined
): Promise<DgStatsCoverageFacts> {
  if (geofips == null || !Number.isFinite(Number(geofips))) {
    return {
      coverageStatus: 'UNAVAILABLE',
      coverageReason: DGSTATS_COVERAGE_UNAVAILABLE_LABEL,
      coveredUtility: null,
      sourceDataset: `California DGStats (${'Interconnected Project Sites'} → all_pv_size)`,
      sourceLatestDate: null,
    };
  }
  const { data, error } = await fetchAllPvSize({
    select: 'year',
    geofips: Number(geofips),
    orderByYear: false,
  });
  if (error || isDgStatsCoverageAbsent(data)) {
    return {
      coverageStatus: 'UNAVAILABLE',
      coverageReason:
        'No California DG Statistics interconnection records for this jurisdiction (common for publicly owned utility service areas). Missing coverage is not zero capacity.',
      coveredUtility: null,
      sourceDataset: `California DGStats (${'Interconnected Project Sites'} → all_pv_size)`,
      sourceLatestDate: null,
    };
  }
  const years = (data || [])
    .map((r: { year?: number | string }) => Number(r.year))
    .filter((y) => Number.isFinite(y));
  const latest = years.length ? Math.max(...years) : null;
  return {
    coverageStatus: 'AVAILABLE',
    coverageReason: '',
    coveredUtility: 'CPUC-jurisdictional IOU reporting (PG&E, SCE, SDG&E) via DGStats',
    sourceDataset: 'California DGStats Interconnected Project Sites (aggregated to all_pv_size / all_pv_cost)',
    sourceLatestDate: latest,
  };
}

export const DGSTATS_COVERAGE_UNAVAILABLE_LABEL =
  'Coverage unavailable or incomplete for this geography in California DG Statistics (investor-owned utility reporting).';

export function isSolarCoverageBlocked(
  facts: DgStatsCoverageFacts | null | undefined
): boolean {
  if (!facts) return false;
  return facts.coverageStatus === 'UNAVAILABLE';
}
